import { DiscardChangeModal } from '@/components';
import Layout from '@/components/layout';
import SkeletonLayout from '@/components/SkeletonLayout';
import { config } from '@/config';
import { PATH } from '@/constants/path';
import useTitleBar from '@/hooks/useTitleBar';
import { useGetDetailLocationQuery } from '@/redux/query';
import {
  handleSaveBackupLocationSettings,
  handleSaveLocationSettings,
  handleSetIsChangeData,
  handleToggleDiscardModal,
  isChangeDataSelector,
  isOpenDiscardModalSelector,
} from '@/redux/slice/location.slice';
import { IResponseApi } from '@/types/apis/response';
import { BlockStack } from '@shopify/polaris';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import FormLocation from './FormLocation';

export default function EditLocation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    data: detailLocation,
    isLoading: isLoadingGetDetailLocation,
    refetch: refetGetDetailLocation,
  } = useGetDetailLocationQuery({ id: Number(id), shop: config.shop }, { skip: !id });
  const isChangeData = useSelector(isChangeDataSelector);
  const isOpenDiscardModal = useSelector(isOpenDiscardModalSelector);

  const handleLeavePage = () => {
    dispatch(handleSetIsChangeData(false));
    dispatch(handleToggleDiscardModal(false));
    setTimeout(() => {
      navigate(PATH.LOCATIONS.pathname);
    }, 100);
  };
  const handleStay = () => dispatch(handleToggleDiscardModal(false));

  const handleBackAction = useCallback(() => {
    if (isChangeData) {
      dispatch(handleToggleDiscardModal(true));
    } else {
      handleLeavePage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChangeData]);

  useEffect(() => {
    dispatch(
      handleSaveLocationSettings({
        id: detailLocation?.data.id,
        shop: detailLocation?.data.shop,
        shopifyLocationId: detailLocation?.data.shopifyLocationId,
        isActive: detailLocation?.data.isActive,
        deletable: detailLocation?.data.deletable,
        activatable: detailLocation?.data.activatable,
        isEnablePickup: detailLocation?.data.isEnablePickup,
        isEnableDelivery: detailLocation?.data.isEnableDelivery,
        name: detailLocation?.data.name,
        updatedAt: detailLocation?.data.updatedAt,
        address: detailLocation?.data.address,
        settings: detailLocation?.data.settings,
      }),
    );
    dispatch(
      handleSaveBackupLocationSettings({
        id: detailLocation?.data.id,
        shop: detailLocation?.data.shop,
        shopifyLocationId: detailLocation?.data.shopifyLocationId,
        isActive: detailLocation?.data.isActive,
        deletable: detailLocation?.data.deletable,
        activatable: detailLocation?.data.activatable,
        isEnablePickup: detailLocation?.data.isEnablePickup,
        isEnableDelivery: detailLocation?.data.isEnableDelivery,
        name: detailLocation?.data.name,
        updatedAt: detailLocation?.data.updatedAt,
        address: detailLocation?.data.address,
        settings: detailLocation?.data.settings,
      }),
    );
    dispatch(handleSetIsChangeData(false));
    dispatch(handleToggleDiscardModal(false));
  }, [detailLocation, dispatch]);

  useTitleBar({
    title: id ? 'Edit location' : 'Add location',
    breadcrumbLabel: 'Locations',
    breadcrumbPath: PATH.LOCATIONS.pathname,
  });

  return (
    <Layout
      title={id ? 'Edit location' : 'Add location'}
      backAction={{
        content: 'locations',
        onAction: handleBackAction,
      }}
    >
      {isLoadingGetDetailLocation ? (
        <SkeletonLayout elementCount={2} />
      ) : (
        <BlockStack gap={'800'}>
          <FormLocation
            detailLocation={detailLocation as IResponseApi.IGetDetailLocation}
            refetGetDetailLocation={refetGetDetailLocation}
          />
        </BlockStack>
      )}
      <DiscardChangeModal
        isOpen={isOpenDiscardModal}
        onClose={() => dispatch(handleToggleDiscardModal(false))}
        onStay={handleStay}
        onLeave={handleLeavePage}
      />
    </Layout>
  );
}
